<section class="setup-card" *ngIf="visible" aria-labelledby="setup-card-title">
  <div class="setup-card__header">
    <h2 id="setup-card-title" class="setup-card__title">Finish setup</h2>
    <button type="button" class="setup-card__dismiss" aria-label="Dismiss setup checklist" (click)="dismiss()">
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
        <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
      </svg>
    </button>
  </div>

  <p class="setup-card__sub">
    {{ singleStepRemaining ? "One more step — send in your first bill to see Synced in action."
                           : "A few quick steps to get Synced working for you." }}
  </p>

  <ul class="setup-card__tasks">
    <!-- ─── Task 1: Workspace ─── -->
    <li class="setup-card__task setup-card__task--done">
      <div class="setup-card__task-row">
        <span class="setup-card__icon setup-card__icon--done" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M3 7.2l2.8 2.8L11 4.4" stroke="var(--brand-yellow)" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
        <div class="setup-card__task-body">
          <div class="setup-card__task-label">Create your workspace</div>
          <div class="setup-card__task-sub">Done during signup</div>
        </div>
      </div>
    </li>

    <!-- ─── Task 2: Connect accounting ─── -->
    <li class="setup-card__task"
        [class.setup-card__task--done]="accountingSectionDone"
        [class.setup-card__task--expanded]="isExpanded('connect_accounting')">
      <button type="button" class="setup-card__task-row"
              [attr.aria-expanded]="isExpanded('connect_accounting')"
              [disabled]="accountingSectionDone"
              (click)="toggle('connect_accounting')">
        <span class="setup-card__icon"
              [class.setup-card__icon--done]="accountingSectionDone"
              [class.setup-card__icon--step]="!accountingSectionDone"
              aria-hidden="true">
          <svg *ngIf="accountingSectionDone" width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M3 7.2l2.8 2.8L11 4.4" stroke="var(--brand-yellow)" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span *ngIf="!accountingSectionDone">2</span>
        </span>
        <div class="setup-card__task-body">
          <div class="setup-card__task-label">Connect Xero or QuickBooks</div>
          <div class="setup-card__task-sub" *ngIf="!accountingSectionDone">So Synced can code transactions to your accounts</div>
          <div class="setup-card__task-sub" *ngIf="accountingDone">
            Connected{{ accountingPlatform === 'QBO' ? ' to QuickBooks' : (accountingPlatform ? ' to ' + accountingPlatform : '') }}
          </div>
          <div class="setup-card__task-sub" *ngIf="!accountingDone && accountingSkipped">
            No accounting integration — skipped
          </div>
        </div>
        <svg *ngIf="!accountingSectionDone" class="setup-card__chevron"
             [class.setup-card__chevron--open]="isExpanded('connect_accounting')"
             width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <path d="M3 5l4 4 4-4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>

      <div class="setup-card__expand" *ngIf="isExpanded('connect_accounting')">
        <div class="setup-card__platforms">
          <article class="setup-card__platform">
            <div class="setup-card__platform-head">
              <img class="setup-card__platform-logo" [src]="xeroPlatform?.logoURL || '../../../../assets/img/Xero.png'" alt="Xero">
              <div>
                <div class="setup-card__platform-name">Xero</div>
                <div class="setup-card__platform-desc">Connect your Xero organisation</div>
              </div>
            </div>
            <button type="button" class="setup-card__btn setup-card__btn--ghost"
                    [disabled]="accountingConnecting"
                    (click)="connectXero()">
              Connect <span aria-hidden="true">→</span>
            </button>
          </article>

          <article class="setup-card__platform">
            <div class="setup-card__platform-head">
              <img class="setup-card__platform-logo" [src]="qboPlatform?.logoURL || '../../../../assets/img/QuickBookConnectLogo.png'" alt="QuickBooks">
              <div>
                <div class="setup-card__platform-name">QuickBooks</div>
                <div class="setup-card__platform-desc">Connect your QuickBooks company</div>
              </div>
            </div>
            <button type="button" class="setup-card__btn setup-card__btn--ghost"
                    [disabled]="accountingConnecting"
                    (click)="connectQbo()">
              Connect <span aria-hidden="true">→</span>
            </button>
          </article>
        </div>

        <p class="setup-card__sample">
          Don't use Xero or QuickBooks?
          <a href="javascript:void(0)" (click)="skipAccounting()">I don't have an accounting integration →</a>
        </p>
      </div>
    </li>

    <!-- ─── Task 3: Process first bill ─── -->
    <li class="setup-card__task"
        [class.setup-card__task--done]="firstBillDone"
        [class.setup-card__task--expanded]="isExpanded('process_first_bill')">
      <button type="button" class="setup-card__task-row"
              [attr.aria-expanded]="isExpanded('process_first_bill')"
              [disabled]="firstBillDone"
              (click)="toggle('process_first_bill')">
        <span class="setup-card__icon"
              [class.setup-card__icon--done]="firstBillDone"
              [class.setup-card__icon--step]="!firstBillDone"
              aria-hidden="true">
          <svg *ngIf="firstBillDone" width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M3 7.2l2.8 2.8L11 4.4" stroke="var(--brand-yellow)" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span *ngIf="!firstBillDone">3</span>
        </span>
        <div class="setup-card__task-body">
          <div class="setup-card__task-label">Process your first bill</div>
          <div class="setup-card__task-sub" *ngIf="!firstBillDone">See Synced code a transaction in seconds</div>
          <div class="setup-card__task-sub" *ngIf="firstBillDone">First bill processed and synced</div>
        </div>
        <svg *ngIf="!firstBillDone" class="setup-card__chevron"
             [class.setup-card__chevron--open]="isExpanded('process_first_bill')"
             width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <path d="M3 5l4 4 4-4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>

      <div class="setup-card__expand" *ngIf="isExpanded('process_first_bill')">
        <div class="setup-card__captures">
          <!-- 1. Upload (primary capture option, lives in the same grid so all
                  four options read as peers) -->
          <article class="setup-card__capture setup-card__capture--upload"
                   [class.setup-card__capture--drag]="dragActive"
                   (dragover)="onDragOver($event)"
                   (dragleave)="onDragLeave($event)"
                   (drop)="onDrop($event)">
            <div class="setup-card__capture-head">
              <span class="setup-card__capture-logo setup-card__capture-logo--mark" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                  <path d="M10 14V4M10 4l-4 4M10 4l4 4M3 16h14" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
              <div>
                <div class="setup-card__capture-name">Upload a bill</div>
                <div class="setup-card__capture-desc">PDF, PNG, or JPG — even a screenshot works</div>
              </div>
            </div>
            <button type="button" class="setup-card__btn setup-card__btn--primary"
                    (click)="triggerUpload('click')">
              Choose file
            </button>
          </article>

          <!-- 2. Forward email -->
          <article class="setup-card__capture">
            <div class="setup-card__capture-head">
              <span class="setup-card__capture-logo setup-card__capture-logo--mark" aria-hidden="true">@</span>
              <div>
                <div class="setup-card__capture-name">Forward email</div>
                <code class="setup-card__forward-addr">{{ forwardingEmail || '—' }}</code>
              </div>
            </div>
            <button type="button" class="setup-card__btn setup-card__btn--ghost"
                    [disabled]="!forwardingEmail"
                    (click)="copyForwardingEmail()">
              {{ copied ? 'Copied ✓' : 'Copy' }}
            </button>
          </article>

          <!-- 3. Gmail Chrome extension -->
          <article class="setup-card__capture" *ngIf="gmailAddOn">
            <div class="setup-card__capture-head">
              <img class="setup-card__capture-logo" [src]="gmailAddOn.logoURL || '../../../../assets/img/Gmail.png'" alt="Gmail">
              <div>
                <div class="setup-card__capture-name">From Gmail</div>
                <div class="setup-card__capture-desc">One-click capture with the Chrome extension</div>
              </div>
            </div>
            <button type="button" class="setup-card__btn setup-card__btn--ghost" (click)="installGmailAddOn()">
              Install <span aria-hidden="true">→</span>
            </button>
          </article>

          <!-- 4. Outlook add-in -->
          <article class="setup-card__capture" *ngIf="outlookAddOn">
            <div class="setup-card__capture-head">
              <img class="setup-card__capture-logo" [src]="outlookAddOn.logoURL || '../../../../assets/img/Outlook.png'" alt="Outlook">
              <div>
                <div class="setup-card__capture-name">From Outlook</div>
                <div class="setup-card__capture-desc">One-click capture with the Outlook add-in</div>
              </div>
            </div>
            <button type="button" class="setup-card__btn setup-card__btn--ghost" (click)="installOutlookAddOn()">
              Install <span aria-hidden="true">→</span>
            </button>
          </article>

          <!-- 5. Synced Go — mobile app -->
          <article class="setup-card__capture">
            <div class="setup-card__capture-head">
              <span class="setup-card__capture-logo setup-card__capture-logo--mark" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                  <rect x="5" y="2" width="10" height="16" rx="2" stroke="currentColor" stroke-width="1.6"/>
                  <circle cx="10" cy="15" r="0.9" fill="currentColor"/>
                </svg>
              </span>
              <div>
                <div class="setup-card__capture-name">Synced Go</div>
                <div class="setup-card__capture-desc">Snap photos of receipts on mobile</div>
              </div>
            </div>
            <button type="button" class="setup-card__btn setup-card__btn--ghost" (click)="openGoModal()">
              Download <span aria-hidden="true">→</span>
            </button>
          </article>
        </div>

        <p class="setup-card__sample">
          No bill handy?
          <a href="javascript:void(0)" (click)="triggerSampleBill()">Try Synced with a sample bill →</a>
        </p>
      </div>
    </li>
  </ul>
</section>

<!-- ─── Synced Go download modal ─── -->
<div class="go-modal-backdrop" *ngIf="goModalOpen" (click)="closeGoModal()">
  <div class="go-modal" role="dialog" aria-modal="true" aria-labelledby="go-modal-title"
       (click)="$event.stopPropagation()">
    <header class="go-modal__header">
      <h3 id="go-modal-title">Download Synced Go</h3>
      <button type="button" class="go-modal__close" aria-label="Close" (click)="closeGoModal()">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
        </svg>
      </button>
    </header>
    <p class="go-modal__lead">
      Scan one of the codes with your phone's camera, or tap a store button to open it directly.
    </p>
    <div class="go-modal__cards">
      <div class="go-modal__card">
        <img class="go-modal__qr" [src]="iosQrUrl" alt="iOS App Store QR code">
        <div class="go-modal__store">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M17.05 12.04c-.03-2.88 2.35-4.27 2.46-4.34-1.34-1.96-3.43-2.23-4.17-2.26-1.77-.18-3.46 1.05-4.36 1.05-.91 0-2.3-1.03-3.78-1-1.94.03-3.75 1.13-4.75 2.86-2.03 3.52-.52 8.74 1.46 11.6.97 1.4 2.12 2.97 3.6 2.92 1.45-.06 2-.94 3.75-.94 1.74 0 2.24.94 3.77.91 1.56-.03 2.55-1.42 3.5-2.83 1.1-1.62 1.56-3.2 1.58-3.28-.04-.02-3.03-1.16-3.06-4.6zM14.13 3.65c.8-.97 1.34-2.32 1.19-3.65-1.15.05-2.54.77-3.37 1.74-.74.86-1.4 2.23-1.22 3.53 1.29.1 2.6-.65 3.4-1.62z"/>
          </svg>
          <div>
            <div class="go-modal__platform">Download on the</div>
            <div class="go-modal__store-name">App Store</div>
          </div>
        </div>
        <button type="button" class="setup-card__btn setup-card__btn--ghost" (click)="openIosStore()">
          Open App Store <span aria-hidden="true">→</span>
        </button>
      </div>

      <div class="go-modal__card">
        <img class="go-modal__qr" [src]="androidQrUrl" alt="Google Play QR code">
        <div class="go-modal__store">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M3 2.5l9.78 9.5L3 21.5c-.5-.3-.8-.8-.8-1.4V3.9c0-.6.3-1.1.8-1.4zm11.4 7.9l2.7-1.5-1.9-1.1L6 2.2l8.4 8.2zm6.6 2.2l-2.8 1.6-3-3 3-3 2.8 1.6c.7.4.7 1.4 0 1.8zM14.4 13.6L6 21.8l9.2-5.3 1.9-1.1-2.7-1.8z"/>
          </svg>
          <div>
            <div class="go-modal__platform">Get it on</div>
            <div class="go-modal__store-name">Google Play</div>
          </div>
        </div>
        <button type="button" class="setup-card__btn setup-card__btn--ghost" (click)="openAndroidStore()">
          Open Google Play <span aria-hidden="true">→</span>
        </button>
      </div>
    </div>
  </div>
</div>
